/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router';
import { gnoment } from 'shared-ui/gnoment';

import {
    ContentContainer,
    useFlags,
    NotFoundPage,
    SecurityHelper,
    announce,
} from 'sarsaparilla';
import { authModalActions } from 'shared-ui/auth/redux';
import { FacilityAlertBannerContainer } from 'shared-ui/containers/FacilityAlertBannerContainer';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';

import { INVENTORY_PARKPASS } from 'ui-field-sales/src/constants/types';

import LEGACY_PassSelectionPage from './LEGACY_PassSelectionPage';
import PassSelection from '../components/PassSelection';
import { fetchFacilityByIdOrSlug, fetchPassTypes, addLocalReservation } from '../actions';
import {
    facilityByIdOrSlugSelectors,
    passTypesSelectors,
    isLoading,
    isLoaded,
} from '../reducers';
import { getParentAsset } from '../api';
import { ReservationDefaults } from '../props';
import useInterval from '../hooks/useInterval';
import { showNewSitePassLayout as showNewSitePassLayoutUtil } from '../utils/launchDarklyUtils';
import { isDailyPassType } from '../utils/date_time_utils';
import { TRANSPORTATION_TYPE } from '../constants';

function PassSelectionPage() {
    const dispatch = useDispatch();
    const [passSelectionPageState, setPassSelectionPageState] = useState({
        selectedPassTypeId: '',
        selectedPass: null,
        parentAsset: null,
        reservation: null,
        submitAttempted: false,
        errorMessage: '',
        status: 'IDLE',
    });
    const params = useParams();
    const { siteId } = params;
    const [searchParams] = useSearchParams();
    const fidParam = searchParams.get('fid');
    const navigate = useNavigate();
    const passSelectionPageRefs = useRef({ didAttemptBuy: false, intervalId: null });
    const isUserLoggedIn = useSelector((state) => !!state.auth?.account?.access_token);
    const userAccount = useSelector((state) => state.auth?.account?.account);
    const isCsrAccount = useSelector((state) => !!state.auth?.csrAccount);
    const facility = useSelector(facilityByIdOrSlugSelectors.getFacilityByIdOrSlug);
    const reservationsErrors = useSelector((state) => state.parkpass.reservations.error);
    const loaded = useSelector((state) =>
        isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
    );
    const loading = useSelector((state) =>
        isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
    );
    const passTypes = useSelector((state) => passTypesSelectors.getPassTypes(state));

    const facilityIdParam = useMemo(() => {
        return siteId || fidParam;
    }, [fidParam, siteId]);

    const isNPSAgency = useMemo(() => facility?.agency === 'NPS', [facility?.agency]);

    const isCommercialPass =
        !!passSelectionPageState?.selectedPass?.features?.['is-commercial-pass'];

    const filteredPassTypes = useMemo(() => {
        const hasPassTypes = !!passTypes?.length;

        if (!hasPassTypes) {
            return passTypes;
        }

        const newPassTypes = passTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return newPassTypes;
    }, [passTypes]);

    const passTypesOptions = useMemo(() => {
        let newPassTypesOptions = {};

        for (let index = 0; index < filteredPassTypes?.length; index += 1) {
            const {
                transportation_type: transportationType,
                transportation_type_name: transportationTypeName,
            } = filteredPassTypes?.[index] ?? {};

            if (
                transportationType !== 'all' &&
                !newPassTypesOptions?.[transportationType]
            ) {
                newPassTypesOptions = {
                    ...newPassTypesOptions,
                    [transportationType]: {
                        transportationType,
                        transportationTypeName,
                        order: TRANSPORTATION_TYPE?.[transportationType]?.order || 9999,
                    },
                };
            }
        }

        const newPassTypesOptionsArray = Object?.values(newPassTypesOptions)?.sort(
            (a, b) => a.order - b.order
        );

        return newPassTypesOptionsArray;
    }, [filteredPassTypes]);

    const getData = useCallback(() => {
        if (!loading.facility && !loaded.facility) {
            dispatch(fetchFacilityByIdOrSlug(facilityIdParam)).then((facilityData) => {
                getParentAsset(facilityData.asset_id).then(({ data }) =>
                    setPassSelectionPageState((prevState) => ({
                        ...prevState,
                        parentAsset: data?.results,
                    }))
                );
                if (facilityData.id && !loading.passTypes && !loaded.passTypes) {
                    dispatch(fetchPassTypes(facilityData.id));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        facilityIdParam,
        loaded.facility,
        loaded.passTypes,
        loading.facility,
        loading.passTypes,
    ]);

    const isLoggedIn = useCallback(() => {
        return (
            SecurityHelper.isLoggedIn() ||
            SecurityHelper.isGuest() ||
            SecurityHelper.isExternalCSRLoggedIn()
        );
    }, []);

    const validateUserLoggedIn = useCallback(() => {
        const loggedIn = isLoggedIn();
        const validUser = !isCsrAccount || (isUserLoggedIn && !SecurityHelper.isGuest());

        return { loggedIn, validUser };
    }, [isCsrAccount, isLoggedIn, isUserLoggedIn]);

    const handleSaveReservation = useCallback(async () => {
        try {
            let errorMessage = '';

            setPassSelectionPageState((prevState) => ({
                ...prevState,
                isSubmitting: true,
                submitAttempted: false,
                errorMessage: '',
                status: 'LOADING',
            }));

            if (!passSelectionPageState?.reservation) {
                errorMessage = 'You must select a pass';
                passSelectionPageRefs.current.didAttemptBuy = false;
                throw new Error(errorMessage);
            }

            const { loggedIn, validUser } = validateUserLoggedIn();

            if (!loggedIn) {
                if (isCommercialPass) {
                    // We disable guest checkout for commercial site passes
                    dispatch(authModalActions.openLoginModal());
                } else {
                    dispatch(authModalActions.openLoginWithGuestModal());
                }
            }

            if (!validUser) {
                errorMessage =
                    'Please select a customer to continue with the reservation.';
                // Note CSR customer selection can not be controlled or influenced here
                // the AuthModal will never open because the CSR is already logged in
            }

            if (!loggedIn || !validUser) {
                passSelectionPageRefs.current.didAttemptBuy = true;
                throw new Error(errorMessage);
            }

            try {
                const reservation = { ...passSelectionPageState?.reservation };

                if (userAccount && !reservation?.pass_info?.holder_info?.email) {
                    reservation.pass_info = {
                        ...reservation.pass_info,
                        holder_info: {
                            first_name: userAccount?.first_name,
                            last_name: userAccount?.last_name,
                            email: userAccount?.email,
                        },
                    };
                    reservation.holder_info = reservation.pass_info.holder_info;
                }

                dispatch(addLocalReservation(reservation));
                setPassSelectionPageState((prevState) => ({
                    ...prevState,
                    status: 'IDLE',
                }));
                passSelectionPageRefs.current.didAttemptBuy = false;
                navigate(`/sitepass/${facilityIdParam}/create/reservation`);
                return true;
            } catch (error) {
                setPassSelectionPageState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    submitAttempted: true,
                    status: 'IDLE',
                }));
                passSelectionPageRefs.current.didAttemptBuy = false;
                window.scrollTo(0, 0);
                return false;
            }
        } catch (error) {
            const newState = {
                isSubmitting: false,
                status: 'IDLE',
            };

            if (error?.message) {
                newState.errorMessage = error?.message;
                announce(error?.message);
            }
            setPassSelectionPageState((prevState) => ({
                ...prevState,
                ...newState,
            }));

            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        facilityIdParam,
        passSelectionPageState?.reservation,
        userAccount,
        validateUserLoggedIn,
    ]);

    const handleRemovePass = useCallback(() => {
        setPassSelectionPageState((prevState) => ({
            ...prevState,
            selectedPassTypeId: '',
            selectedPass: null,
            reservation: null,
            errorMessage: '',
            submitAttempted: false,
        }));
        passSelectionPageRefs.current.didAttemptBuy = false;
    }, []);

    const checkModals = useCallback(() => {
        // We need to check if a modal is open to reset the selected pass, since, We don't have a way to know when the login modal was closed
        const hasOpenModals = document.getElementsByClassName('ReactModal__Body--open');
        if (passSelectionPageRefs?.current?.didAttemptBuy && !hasOpenModals?.length) {
            handleRemovePass();
        }
    }, [handleRemovePass]);

    useInterval(checkModals, 500);

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const errorMessage = reservationsErrors?.[0]?.error_message;
        if (errorMessage) {
            setPassSelectionPageState((prevState) => ({
                ...prevState,
                errorMessage,
            }));
        }
    }, [reservationsErrors]);

    useEffect(() => {
        const { loggedIn, validUser } = validateUserLoggedIn();

        if (loggedIn && validUser && passSelectionPageRefs?.current?.didAttemptBuy) {
            handleSaveReservation();
        }
    }, [
        handleSaveReservation,
        isCsrAccount,
        isLoggedIn,
        isUserLoggedIn,
        validateUserLoggedIn,
    ]);

    const handleSelectPass = useCallback(
        (id) => {
            let passInfo = {};

            if (userAccount) {
                passInfo = {
                    pass_info: {
                        ...ReservationDefaults.pass_info,
                        holder_info: {
                            first_name: userAccount?.first_name,
                            last_name: userAccount?.last_name,
                            email: userAccount?.email,
                        },
                    },
                };
            }

            let newReservation = {
                ...ReservationDefaults,
                is_pending: true,
                facility_id: facilityIdParam,
                pass_type_id: id,
                ...passInfo,
            };
            newReservation.pass_info.start_date = gnoment().startOf('day').format();
            newReservation.pass_info.features['start-date'] = gnoment()
                .startOf('day')
                .format();

            const currentPassType = filteredPassTypes?.find((item) => item?.id === id);

            newReservation.pass_info.duration = currentPassType?.duration;

            if (currentPassType && isDailyPassType(currentPassType?.duration)) {
                newReservation.pass_info.start_date = '';
                newReservation.pass_info.features['start-date'] = '';
            }

            // If the pass type has the non_resident_fee feature enabled, set the default
            if (currentPassType?.features?.non_resident_fee?.fee !== 0) {
                newReservation.pass_info.features['non_resident_fee'] = { quantity: 0 };
            }

            newReservation = {
                ...newReservation,
                holder_info: newReservation?.pass_info?.holder_info,
                features: {
                    discount: {},
                    ...newReservation?.pass_info?.features,
                },
            };

            setPassSelectionPageState((prevState) => ({
                ...prevState,
                selectedPassTypeId: id,
                selectedPass: currentPassType,
                reservation: newReservation,
                errorMessage: '',
                submitAttempted: false,
            }));
        },
        [facilityIdParam, filteredPassTypes, userAccount]
    );

    // Fire the view_item GA Event
    useSendGAEvent({
        itemLevel: 'Pass Selection',
        facilityId: facility?.asset_id,
        itemCategory: INVENTORY_PARKPASS,
    });

    if (
        (loaded?.facilityByIdOrSlug && !loading?.facilityByIdOrSlug && !facility?.id) ||
        facility?.is_external_search_disabled
    ) {
        return <NotFoundPage />;
    }

    // TBD - We could remove this later if needed
    if (!isNPSAgency) {
        return <LEGACY_PassSelectionPage />;
    }

    return (
        <ContentContainer className="pass-selection-page">
            <FacilityAlertBannerContainer className="mb-4 has-full-border" />
            <PassSelection
                facility={facility}
                facilityId={facilityIdParam}
                parentAsset={passSelectionPageState?.parentAsset}
                loaded={loaded}
                loading={loading}
                selectedPassTypeId={passSelectionPageState?.selectedPassTypeId}
                selectedPass={passSelectionPageState?.selectedPass}
                passTypes={filteredPassTypes}
                passTypesRaw={passTypes}
                errorMessage={passSelectionPageState?.errorMessage}
                status={passSelectionPageState?.status}
                passTypesOptions={passTypesOptions}
                onSelectPass={handleSelectPass}
                onRemovePass={handleRemovePass}
                onSaveReservation={handleSaveReservation}
            />
        </ContentContainer>
    );
}

function PassSelectionPageWrapper(props) {
    const { sitePassUiRevamp } = useFlags();
    const shouldShowNewSitePassLayout = useMemo(() => {
        return showNewSitePassLayoutUtil(sitePassUiRevamp);
    }, [sitePassUiRevamp]);

    if (!shouldShowNewSitePassLayout) {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <LEGACY_PassSelectionPage {...props} />;
    }

    return <PassSelectionPage />;
}

export default PassSelectionPageWrapper;
